/**
 * ViewportHUD — Spy Cockpit Tactical Overlay
 *
 * Pure CSS heads-up display rendered on top of the 3D globe.
 * Bracket corners, crosshair, scan line, compass bar, edge readouts.
 * Desktop only — hidden via CSS on mobile ≤ 768px.
 */

import { useMemo } from 'react';
import { useGlobeStore } from '../../stores/globeStore';
import styles from './ViewportHUD.module.css';

// ─── Compass Strip Data ──────────────────────────────────────────

/** Compass label at given degree. Cardinal/intercardinal + every 30° */
const COMPASS_LABELS: Record<number, string> = {
  0: 'N', 30: '30', 60: '60', 90: 'E', 120: '120', 150: '150',
  180: 'S', 210: '210', 240: '240', 270: 'W', 300: '300', 330: '330',
};

const INTERCARDINAL_LABELS: Record<number, string> = {
  45: 'NE', 135: 'SE', 225: 'SW', 315: 'NW',
};

/** Spacing: pixels per degree. Higher = wider compass. */
const PX_PER_DEGREE = 4;

/** Total width for one 360° pass */
const STRIP_WIDTH = 360 * PX_PER_DEGREE;

function isCardinal(deg: number): boolean {
  return deg === 0 || deg === 90 || deg === 180 || deg === 270;
}

/** Build the tick/label elements for one 360° pass */
function buildCompassTicks(offset: number) {
  const elements: JSX.Element[] = [];
  for (let deg = 0; deg < 360; deg += 5) {
    const x = offset + deg * PX_PER_DEGREE;
    const label = COMPASS_LABELS[deg];
    const interLabel = INTERCARDINAL_LABELS[deg];
    const cardinal = isCardinal(deg);
    const isMajor = deg % 10 === 0;

    // Tick mark
    elements.push(
      <div
        key={`t-${offset}-${deg}`}
        className={
          cardinal ? styles.compassTickCardinal
          : isMajor ? styles.compassTickMajor
          : styles.compassTick
        }
        style={{ left: `${x}px` }}
      />,
    );

    // Label
    if (label) {
      elements.push(
        <span
          key={`l-${offset}-${deg}`}
          className={cardinal ? styles.compassLabelCardinal : styles.compassLabel}
          style={{ left: `${x}px` }}
        >
          {label}
        </span>,
      );
    } else if (interLabel) {
      elements.push(
        <span
          key={`il-${offset}-${deg}`}
          className={styles.compassLabelInter}
          style={{ left: `${x}px` }}
        >
          {interLabel}
        </span>,
      );
    }
  }
  return elements;
}

export default function ViewportHUD() {
  const cameraPosition = useGlobeStore((s) => s.cameraPosition);

  // Format camera values for edge readouts
  const alt = cameraPosition.alt > 10000
    ? `${(cameraPosition.alt / 1000).toFixed(1)} km`
    : `${Math.round(cameraPosition.alt)} m`;
  const hdg = `${cameraPosition.heading.toFixed(1)}°`;
  const pitch = `${cameraPosition.pitch.toFixed(1)}°`;

  // Compass heading → strip offset. heading=0 means N centered.
  const heading = cameraPosition.heading;
  const stripOffset = -(heading * PX_PER_DEGREE);

  // Build compass strip (memoize the static tick elements)
  const compassTicks = useMemo(() => [
    ...buildCompassTicks(0),
    ...buildCompassTicks(STRIP_WIDTH),   // duplicate for seamless wrap
  ], []);

  return (
    <div className={styles.hud} aria-hidden="true">
      {/* ── Bracket Corners ── */}
      <div className={`${styles.corner} ${styles.cornerTL}`} />
      <div className={`${styles.corner} ${styles.cornerTR}`} />
      <div className={`${styles.corner} ${styles.cornerBL}`} />
      <div className={`${styles.corner} ${styles.cornerBR}`} />

      {/* ── Frame Lines ── */}
      <div className={`${styles.frameLine} ${styles.frameLineTop}`} />
      <div className={`${styles.frameLine} ${styles.frameLineBottom}`} />
      <div className={`${styles.frameLine} ${styles.frameLineLeft}`} />
      <div className={`${styles.frameLine} ${styles.frameLineRight}`} />

      {/* ── Scan Line ── */}
      <div className={styles.scanLine} />

      {/* ── Compass Bar ── */}
      <div className={styles.compassContainer}>
        {/* Center chevron indicator */}
        <div className={styles.compassChevron}>▼</div>
        {/* Heading readout */}
        <div className={styles.compassHeading}>{Math.round(heading)}°</div>
        {/* Scrolling strip */}
        <div className={styles.compassWindow}>
          <div
            className={styles.compassStrip}
            style={{ transform: `translateX(${stripOffset}px)` }}
          >
            {compassTicks}
          </div>
        </div>
      </div>

      {/* ── Edge Data Readouts ── */}
      <div className={`${styles.edgeData} ${styles.edgeDataTopRight}`}>
        ALT {alt}<br />
        HDG {hdg}
      </div>
      <div className={`${styles.edgeData} ${styles.edgeDataBottomLeft}`}>
        PITCH {pitch}
      </div>
    </div>
  );
}
